import { useState, useRef, useEffect } from 'react';

const MORE_MIN = 4;
const MORE_MAX = 8;

const buttonClass = (active) =>
  `min-w-[2.75rem] px-4 py-2 rounded-lg text-sm font-medium border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-terra-400 focus:ring-offset-1 ${
    active
      ? 'bg-terra-400 text-white border-terra-400'
      : 'bg-white text-ink-700 border-sand-200 hover:border-terra-400'
  }`;

// Preset 1/2/3 buttons + a "3+" option that turns into an inline number input (4-8) when picked.
export default function CountSelector({ label, unit, value, onChange, required }) {
  const [mode, setMode] = useState(() => {
    if (value === 1 || value === 2 || value === 3) return String(value);
    if (typeof value === 'number' && value >= MORE_MIN) return 'more';
    return null;
  });
  const [moreText, setMoreText] = useState(() => (
    typeof value === 'number' && value >= MORE_MIN ? String(value) : ''
  ));
  const [moreError, setMoreError] = useState('');
  const moreInputRef = useRef(null);

  useEffect(() => {
    if (mode === 'more') moreInputRef.current?.focus();
  }, [mode]);

  const selectPreset = (n) => {
    setMode(String(n));
    setMoreText('');
    setMoreError('');
    onChange(n);
  };

  const selectMore = () => {
    setMode('more');
    setMoreText('');
    setMoreError('');
    onChange('');
  };

  // Let the user type freely — validate on top instead of clamping as they go.
  const handleMoreInput = (e) => {
    const raw = e.target.value;
    setMoreText(raw);

    if (raw === '') {
      setMoreError('');
      onChange('');
      return;
    }

    const n = Number(raw);
    if (!Number.isInteger(n) || n < MORE_MIN || n > MORE_MAX) {
      setMoreError(`Must be a whole number between ${MORE_MIN} and ${MORE_MAX}`);
      onChange('');
      return;
    }

    setMoreError('');
    onChange(n);
  };

  return (
    <div>
      <label className="text-sm font-medium text-ink-700 block mb-2">
        {label}{required ? '' : ' (optional)'}
      </label>

      <div className="flex flex-wrap items-center gap-2" role="group" aria-label={label}>
        {[1, 2, 3].map(n => (
          <button
            key={n}
            type="button"
            aria-pressed={mode === String(n)}
            onClick={() => selectPreset(n)}
            className={buttonClass(mode === String(n))}
          >
            {n}
          </button>
        ))}

        {mode === 'more' ? (
          <input
            ref={moreInputRef}
            type="number"
            inputMode="numeric"
            step={1}
            value={moreText}
            onChange={handleMoreInput}
            placeholder={`${MORE_MIN}-${MORE_MAX}`}
            required={required}
            aria-invalid={!!moreError}
            aria-label={`${label} (${MORE_MIN} to ${MORE_MAX})`}
            className={`w-20 px-3 py-2 rounded-lg text-sm font-medium text-center border bg-white text-ink-900 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
              moreError ? 'border-uae-red focus:ring-uae-red' : 'border-terra-400 focus:ring-terra-400'
            }`}
          />
        ) : (
          <button
            type="button"
            aria-pressed={false}
            onClick={selectMore}
            className={buttonClass(false)}
          >
            3+
          </button>
        )}
      </div>

      {mode === 'more' && (
        <p className={`text-xs mt-2 ${moreError ? 'text-uae-red' : 'text-ink-400'}`}>
          {moreError || `Enter a number between ${MORE_MIN} and ${MORE_MAX}${unit ? ` ${unit}` : ''}.`}
        </p>
      )}
    </div>
  );
}
